/**
 * Offline click queue — small IndexedDB wrapper.
 *
 * Behaviour:
 *   - Every mutating call (click / undo) checks `navigator.onLine`.
 *   - When offline, the call is written to the "pending" store with a
 *     timestamp and a monotonically increasing `id` (auto-key). The UI
 *     shows an "Offline — N pending" chip.
 *   - When we come back online, `drainQueue()` replays each pending
 *     item as an individual API call, in insertion order, then removes
 *     it from the store. Failures leave the item in the queue so the
 *     next reconnect retries it.
 *
 * We deliberately keep this dependency-free (no `idb` package) so the
 * bundle stays tiny — the whole surface is: enqueue / all / remove /
 * drain / subscribe.
 */

const DB_NAME = "refclick";
const DB_VERSION = 1;
const STORE = "pending_clicks";

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(mode) {
  const db = await openDb();
  return db.transaction(STORE, mode).objectStore(STORE);
}

/** Add a { link_id, kind: 'click'|'undo' } record. Returns the row id. */
export async function enqueue(op) {
  try {
    const store = await tx("readwrite");
    return await new Promise((resolve, reject) => {
      const req = store.add({
        link_id: op.link_id,
        kind: op.kind,
        queued_at: Date.now(),
      });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

/** Return all pending rows in insertion order. */
export async function all() {
  try {
    const store = await tx("readonly");
    return await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

/** Remove one row by primary key. */
export async function remove(id) {
  try {
    const store = await tx("readwrite");
    return await new Promise((resolve, reject) => {
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    /* swallow */
  }
}

/** Number of pending rows. */
export async function count() {
  const rows = await all();
  return rows.length;
}

/**
 * Drain the queue by executing `run(op)` for each pending row in order.
 * `run` should be an async function that performs the API call and
 * resolves on success or throws on failure. Successful ops are removed
 * from the queue; the first failure stops the drain (queue is preserved).
 */
export async function drainQueue(run) {
  const rows = await all();
  let successCount = 0;
  for (const row of rows) {
    try {
      await run(row);
      await remove(row.id);
      successCount += 1;
    } catch {
      break; // stop on first failure; retry on next reconnect
    }
  }
  return successCount;
}

// --- Simple pub/sub so the header chip re-renders on queue changes -------
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* keep others running */
    }
  }
}
